import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { InputText } from "../../InputText";
import { TextArea } from "../../TextArea";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { GraduationCap, Loader2, X } from "lucide-react";
import { DatePickerField } from "../../DatePickerField";
import { turmasAPI } from "../../../services/turmas";
import { GrupoTurma } from "../../../types/turmas";

interface FormValues {
  nome: string;
  descricao: string;
  data_inicio: string;
  data_fim: string;
  ativa: boolean;
}

interface TurmaModalProps {
  isOpen: boolean;
  onClose: () => void;
  idGrupo: string;
  editingTurma?: GrupoTurma | null;
  onSuccess?: () => void;
}

const validationSchema = Yup.object({
  nome: Yup.string()
    .required("Nome da turma é obrigatório")
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(100, "Nome deve ter no máximo 100 caracteres"),
  descricao: Yup.string()
    .max(500, "Descrição deve ter no máximo 500 caracteres"),
  data_inicio: Yup.date()
    .required("Data de início é obrigatória"),
  data_fim: Yup.date()
    .nullable()
    .min(Yup.ref('data_inicio'), "Data de fim deve ser após a data de início"),
  ativa: Yup.boolean(),
});

export function TurmaModal({ isOpen, onClose, idGrupo, editingTurma = null, onSuccess }: TurmaModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormValues>({
    nome: "",
    descricao: "",
    data_inicio: "",
    data_fim: "",
    ativa: true,
  });
  const [errors, setErrors] = useState<Partial<FormValues>>({});

  useEffect(() => {
    if (editingTurma) {
      setFormData({
        nome: editingTurma.nome,
        descricao: editingTurma.descricao || "",
        data_inicio: editingTurma.data_inicio.split('T')[0],
        data_fim: editingTurma.data_fim ? editingTurma.data_fim.split('T')[0] : "",
        ativa: editingTurma.ativa,
      });
    } else {
      setFormData({
        nome: "",
        descricao: "",
        data_inicio: new Date().toISOString().split('T')[0],
        data_fim: "",
        ativa: true,
      });
    }
    setErrors({});
  }, [editingTurma, isOpen]);

  const validateForm = () => {
    try {
      validationSchema.validateSync(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error: any) {
      const validationErrors: any = {};
      error.inner.forEach((err: any) => {
        if (err.path) {
          validationErrors[err.path] = err.message;
        }
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        id_grupo: idGrupo,
        nome: formData.nome,
        descricao: formData.descricao || undefined,
        data_inicio: formData.data_inicio,
        data_fim: formData.data_fim || undefined,
        ativa: formData.ativa,
      };

      if (editingTurma) {
        await turmasAPI.atualizar(editingTurma.id, payload);
        enqueueSnackbar("Turma atualizada com sucesso!", { variant: "success" });
      } else {
        await turmasAPI.criar(payload);
        enqueueSnackbar("Turma criada com sucesso!", { variant: "success" });
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao salvar turma", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      nome: "",
      descricao: "",
      data_inicio: new Date().toISOString().split('T')[0],
      data_fim: "",
      ativa: true
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {editingTurma ? "Editar Turma" : "Nova Turma"}
            </CardTitle>
            <CardDescription>
              {editingTurma
                ? "Atualize as informações da turma"
                : "Crie uma nova turma para organizar os membros"}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nome">Nome da Turma</Label>
              <InputText
                id="nome"
                name="nome"
                title=""
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: Turma 2026-A, Primeira Turma, etc."
                error={errors.nome}
                className="mt-1"
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="descricao">Descrição (opcional)</Label>
              <TextArea
                id="descricao"
                name="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Descreva o objetivo ou características desta turma..."
                error={errors.descricao}
                className="mt-1"
                disabled={loading}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <DatePickerField
                  label="Data de Início"
                  id="data_inicio"
                  name="data_inicio"
                  value={formData.data_inicio}
                  onChange={(value) => setFormData({ ...formData, data_inicio: value })}
                  required
                  disabled={loading}
                  error={errors.data_inicio}
                />
              </div>

              <div>
                <DatePickerField
                  label="Data de Fim (opcional)"
                  id="data_fim"
                  name="data_fim"
                  value={formData.data_fim}
                  onChange={(value) => setFormData({ ...formData, data_fim: value })}
                  disabled={loading}
                  error={errors.data_fim}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                id="ativa"
                type="checkbox"
                checked={formData.ativa}
                onChange={(e) => setFormData({ ...formData, ativa: e.target.checked })}
                className="w-4 h-4 text-[oklch(0.45_0.15_142)] bg-background border-border rounded focus:ring-ring focus:ring-2"
                disabled={loading}
              />
              <Label htmlFor="ativa" className="cursor-pointer">
                Turma Ativa
              </Label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingTurma ? "Atualizando..." : "Criando..."}
                  </>
                ) : (
                  editingTurma ? "Atualizar" : "Criar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
